import type { PageKey } from './site';

export type AnswerLink = { label: string; href: string };
export type PageAnswers = {
  key: PageKey;
  route: string;
  heading: string;
  summary: string;
  faqs: { question: string; answer: string; links: AnswerLink[] }[];
  cta: AnswerLink;
};

// Exact customer-review copy, verified against the approved service scope.
// These visible answers do not imply a ranking or AI-citation guarantee.
export const pageAnswers: Partial<Record<PageKey, PageAnswers>> = {
  "home": {
    "key": "home",
    "route": "/",
    "heading": "Landscaping questions, answered.",
    "summary": "Based in Livonia, Michigan, D.A. Alexander & Company provides residential landscape design and installation, development planting and stabilization, commercial landscape care, and commercial and seasonal services for businesses and HOA communities in the surrounding southeast Michigan area.",
    "faqs": [
      {
        "question": "Can you help with both new and existing landscapes?",
        "answer": "Residential services include new landscape design and installation as well as landscape renovation. Planting, patios, walkways, retaining walls and outdoor features can be considered together, whether you are starting a new garden or improving an established one.",
        "links": [
          {
            "label": "Explore residential landscapes",
            "href": "/residential-landscapes"
          }
        ]
      },
      {
        "question": "Who is ongoing property care for?",
        "answer": "Landscape maintenance, lawn programs and snow services are offered for businesses and homeowners associations. Commercial landscape care covers growing-season grounds work; commercial and seasonal services cover winter, paved-area and active-site needs.",
        "links": [
          {
            "label": "Commercial landscape care",
            "href": "/commercial-landscape-care"
          },
          {
            "label": "Commercial and seasonal services",
            "href": "/commercial-seasonal-services"
          }
        ]
      },
      {
        "question": "Where can I see examples of your work?",
        "answer": "The project gallery shows selected residential landscapes in Northville, Canton and Plymouth. The Awards page pairs those featured landscapes with their 2019 Michigan Green Industry Association recognition, so you can see the work alongside its award details.",
        "links": [
          {
            "label": "View selected projects",
            "href": "/gallery"
          },
          {
            "label": "Explore award recognition",
            "href": "/awards"
          }
        ]
      }
    ],
    "cta": {
      "label": "Find your service",
      "href": "/services"
    }
  },
  "services": {
    "key": "services",
    "route": "/services",
    "heading": "Which service fits your property?",
    "summary": "Start with the property and the work you need. Residential landscapes and land development lead D.A. Alexander’s services, while commercial landscape care and commercial and seasonal services support the business properties and HOA grounds you manage.",
    "faqs": [
      {
        "question": "Which services are for a home landscape?",
        "answer": "Residential landscapes and outdoor living cover new planting, garden renovation, brick pavers and hardscape, outdoor kitchens and living areas, fire features and landscape lighting. This is the place to start for creating or improving the outdoor spaces around a home.",
        "links": [
          {
            "label": "Residential landscapes and outdoor living",
            "href": "/residential-landscapes"
          }
        ]
      },
      {
        "question": "How is land development different from construction-site service?",
        "answer": "Land development focuses on the landscape: site planting, street trees, ground cover and stabilization. Construction-site services cover tasks such as sweeping, heavy-debris cleanup and silt-fence maintenance. The two pages describe these different types of work.",
        "links": [
          {
            "label": "Land development",
            "href": "/land-development"
          },
          {
            "label": "Construction-site services",
            "href": "/commercial-seasonal-services#construction-sites"
          }
        ]
      },
      {
        "question": "Which page should a business or HOA use?",
        "answer": "Choose commercial landscape care for mowing, bed care, pruning, seasonal cleanup and lawn programs. Choose commercial and seasonal services for snow and ice management, parking-lot and road care, or construction-site support. You can describe both needs in your inquiry.",
        "links": [
          {
            "label": "Commercial landscape care",
            "href": "/commercial-landscape-care"
          },
          {
            "label": "Commercial and seasonal services",
            "href": "/commercial-seasonal-services"
          }
        ]
      }
    ],
    "cta": {
      "label": "Start a conversation",
      "href": "#service-inquiry"
    }
  },
  "residential": {
    "key": "residential",
    "route": "/residential-landscapes",
    "heading": "Questions about your residential landscape.",
    "summary": "A residential landscape can bring planting, stonework and outdoor living into one connected space. D.A. Alexander offers design and installation, renovation, hardscape, outdoor kitchens and living areas, fire features and lighting for the places you enjoy at home.",
    "faqs": [
      {
        "question": "Can you improve an established landscape?",
        "answer": "Yes. Landscape renovation can focus on overgrown planting, an entrance, a worn path or a retaining wall. Tell us which parts of your garden you want to rework and what you would like to enjoy or use differently.",
        "links": [
          {
            "label": "Landscape renovation",
            "href": "#landscape-renovation"
          }
        ]
      },
      {
        "question": "Can planting and hardscape be planned together?",
        "answer": "Landscape design can combine trees, shrubs and perennial planting with patios, walkways, steps and retaining walls. Planning these elements together helps connect the different parts of the property, rather than treating a path, patio and garden as unrelated additions.",
        "links": [
          {
            "label": "Planting and landscape design",
            "href": "#landscape-design-installation"
          },
          {
            "label": "Brick pavers and hardscape",
            "href": "#hardscape"
          }
        ]
      },
      {
        "question": "What should I share about a residential project?",
        "answer": "Include your property location, whether you are planning a new landscape or changing an existing one, and the features you have in mind. Planting, hardscape or outdoor-living ideas give the conversation a useful starting point; your notes do not need to be formal.",
        "links": [
          {
            "label": "Tell us about your property",
            "href": "#service-inquiry"
          }
        ]
      }
    ],
    "cta": {
      "label": "Discuss your landscape",
      "href": "#service-inquiry"
    }
  },
  "landDevelopment": {
    "key": "landDevelopment",
    "route": "/land-development",
    "heading": "Questions about development landscapes.",
    "summary": "D.A. Alexander’s land development work focuses on planting, ground cover and landscape stabilization around new sites and communities. Work includes site plantings, street trees, entry features, seeding, sod, and stabilization for areas such as ponds and swales.",
    "faqs": [
      {
        "question": "How do hydroseeding, seeding and sod differ?",
        "answer": "Hydroseeding applies seed in a sprayed mixture with mulch. Conventional seeding distributes seed over prepared soil, while sod installation lays pre-grown turf. D.A. Alexander offers all three. Soil, slope, season and moisture are useful site details to discuss when choosing an approach.",
        "links": [
          {
            "label": "Seeding and sod",
            "href": "#seeding-sod"
          }
        ]
      },
      {
        "question": "What stabilization work is included?",
        "answer": "Stabilization services include soil stabilization, pond and swale stabilization, erosion matting and straw blowing. Describe the areas you need to establish or protect when you inquire, along with the planting and ground-cover work you have in mind.",
        "links": [
          {
            "label": "Stabilization services",
            "href": "#stabilization"
          }
        ]
      },
      {
        "question": "Where can I find construction-site cleanup?",
        "answer": "Construction-site sweeping, heavy-debris scraping and cleanup, curb and exterior cleanup, and silt-fence or silt-sac maintenance are covered under commercial and seasonal services. Explore that page for the active-site tasks alongside your landscape planting needs.",
        "links": [
          {
            "label": "Construction-site services",
            "href": "/commercial-seasonal-services#construction-sites"
          }
        ]
      }
    ],
    "cta": {
      "label": "Discuss your site",
      "href": "#service-inquiry"
    }
  },
  "commercialCare": {
    "key": "commercialCare",
    "route": "/commercial-landscape-care",
    "heading": "Questions about commercial grounds care.",
    "summary": "D.A. Alexander provides lawn and landscape maintenance for business properties, HOA common areas and shared community grounds. The work includes routine landscape upkeep, seasonal attention and lawn programs, with separate commercial services for snow, paved areas and active sites.",
    "faqs": [
      {
        "question": "What does routine landscape care include?",
        "answer": "Routine care includes lawn mowing, trimming and edging, landscape-bed care, and tree and shrub pruning. These services address the lawn areas, planting beds and landscape features around commercial properties and the shared spaces of HOA communities.",
        "links": [
          {
            "label": "Routine landscape care",
            "href": "#routine-landscape-care"
          }
        ]
      },
      {
        "question": "What seasonal care and lawn programs do you offer?",
        "answer": "Seasonal upkeep includes spring and fall cleanup and annual and perennial flower care. Lawn-program services include fertilization and weed control, grub prevention and core aeration. Tell us which of these services you need for the grounds you manage.",
        "links": [
          {
            "label": "Seasonal upkeep",
            "href": "#seasonal-upkeep"
          },
          {
            "label": "Lawn programs",
            "href": "#lawn-programs"
          }
        ]
      },
      {
        "question": "What information helps with a maintenance inquiry?",
        "answer": "Share the property location, whether it is a business property or HOA common area, and the maintenance or lawn-program work you are looking for. If winter care is also needed, include that in your message and review the separate commercial and seasonal services.",
        "links": [
          {
            "label": "Commercial and seasonal services",
            "href": "/commercial-seasonal-services"
          }
        ]
      }
    ],
    "cta": {
      "label": "Discuss property care",
      "href": "#service-inquiry"
    }
  },
  "commercialSeasonal": {
    "key": "commercialSeasonal",
    "route": "/commercial-seasonal-services",
    "heading": "Questions about seasonal and site services.",
    "summary": "Commercial and seasonal services cover snow and ice management, business parking areas and active construction sites. D.A. Alexander’s maintenance and snow services are for businesses and HOA communities, with work grouped by winter, paved-area and site needs.",
    "faqs": [
      {
        "question": "What snow and ice services do you offer?",
        "answer": "Winter services include snow plowing, sidewalk snow cleanup, road-salt applications, walkway ice-melt application, loader services and snow hauling from the site. Tell us about the commercial property or HOA community and the areas you need cared for.",
        "links": [
          {
            "label": "Winter property care",
            "href": "#winter-care"
          }
        ]
      },
      {
        "question": "What services are available beyond winter?",
        "answer": "Parking-lot and road services include power-vacuum sweeping and cleaning, pavement maintenance and repairs, parking signs and striping, and power washing. Construction-site work includes street sweeping, heavy-debris cleanup, curb and exterior cleanup, and silt-fence or silt-sac maintenance.",
        "links": [
          {
            "label": "Parking lots and roads",
            "href": "#parking-lots-roads"
          },
          {
            "label": "Construction sites",
            "href": "#construction-sites"
          }
        ]
      },
      {
        "question": "What should I include in a commercial-service inquiry?",
        "answer": "Share the property or site location, the type of property you manage and the tasks you need help with. Identify whether the request concerns winter access areas, paved surfaces or an active construction site so the work you have in mind is clear.",
        "links": [
          {
            "label": "Describe your property or site",
            "href": "#service-inquiry"
          }
        ]
      }
    ],
    "cta": {
      "label": "Discuss your property",
      "href": "#service-inquiry"
    }
  },
  "contact": {
    "key": "contact",
    "route": "/contact",
    "heading": "Before you get in touch.",
    "summary": "Use the inquiry form to describe your property and the work you have in mind, or call D.A. Alexander’s Livonia office at 734-427-0030. The office details and service-area list on this page can help you prepare your inquiry.",
    "faqs": [
      {
        "question": "What information should I include in my message?",
        "answer": "Include the property location, the type of property and the services you are interested in. Tell us what you would like to create, improve or care for. For maintenance or snow services, identify the business property or HOA grounds you manage.",
        "links": [
          {
            "label": "Write your inquiry",
            "href": "#contact"
          }
        ]
      },
      {
        "question": "What are the office hours?",
        "answer": "The office is open Monday through Friday, 8AM to 5PM, and closed Saturday and Sunday. Call 734-427-0030, or use the inquiry form to describe your property and the landscape or commercial services you need.",
        "links": [
          {
            "label": "Call 734-427-0030",
            "href": "tel:+17344270030"
          }
        ]
      },
      {
        "question": "Where is D.A. Alexander based?",
        "answer": "The office is at 12711 Farmington Road, Livonia, MI 48150. D.A. Alexander serves the southeast Michigan communities in the service-area list, including West Bloomfield and Birmingham, along with surrounding communities. Include your property location when asking about your project.",
        "links": [
          {
            "label": "View the service area",
            "href": "#service-area"
          }
        ]
      }
    ],
    "cta": {
      "label": "Call 734-427-0030",
      "href": "tel:+17344270030"
    }
  }
};
